import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { Send } from '@material-ui/icons';
import { Field, Form, Formik, FieldProps } from 'formik';
import { object, string } from 'yup';
import { convertFromRaw, EditorState } from 'draft-js';
import { CampfireRichEditor } from '@campfire/campfire-rich-editor';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';

const MAX_FORM_WIDTH = 900;

interface TaskField {
  taskFieldId: string;
  fieldName: string;
  fieldType: string;
  isRequired: boolean;
  selectOptions: string[] | null;
  allowsMultiple: boolean;
  placeholder: string | null;
  helpText: string | null;
}

interface RefereeCheckFormProps {
  volunteerName: string;
  taskTitle: string;
  instructions?: string | null;
  taskFields: TaskField[];
  refereeMessage?: string | null;
  onSubmit: (fieldValues: Array<{ taskFieldId: string; value: string }>) => void;
  isSubmitting: boolean;
  orgLogo?: string | null;
  orgName?: string | null;
}

export function RefereeCheckForm({
  volunteerName,
  taskTitle,
  instructions,
  taskFields,
  refereeMessage,
  onSubmit,
  isSubmitting,
  orgLogo,
  orgName,
}: RefereeCheckFormProps) {
  const { isMobile } = useCampfireTheme();

  // Convert rich text instructions to editor state for display
  const instructionsEditorState = useMemo(() => {
    if (!instructions) return null;

    try {
      // Try to parse as rich text JSON
      const contentState = convertFromRaw(JSON.parse(instructions));
      return EditorState.createWithContent(contentState);
    } catch {
      // If it's not JSON, treat as plain text
      return null;
    }
  }, [instructions]);

  // Convert rich text referee message to editor state for display
  const refereeMessageEditorState = useMemo(() => {
    if (!refereeMessage) return null;

    try {
      // Try to parse as rich text JSON
      const contentState = convertFromRaw(JSON.parse(refereeMessage));
      return EditorState.createWithContent(contentState);
    } catch {
      // If it's not JSON, treat as plain text
      return null;
    }
  }, [refereeMessage]);

  // Create initial values and validation schema based on task fields
  const initialValues: Record<string, string> = {};
  const validationFields: Record<string, any> = {};

  taskFields.forEach((field) => {
    initialValues[field.taskFieldId] = '';
    if (field.isRequired) {
      validationFields[field.taskFieldId] = string().required('Field required');
    } else {
      validationFields[field.taskFieldId] = string();
    }
  });

  const validationSchema = object().shape(validationFields);

  const handleSubmit = (values: Record<string, string>) => {
    // Use taskFields order to ensure consistent field ordering in submission
    const fieldValues = taskFields.map((field) => ({
      taskFieldId: field.taskFieldId,
      value: values[field.taskFieldId] || '',
    }));
    onSubmit(fieldValues);
  };

  return (
    <Box maxWidth={MAX_FORM_WIDTH} width={1} paddingLeft={1}>
      {/* Personal message from volunteer */}
      {refereeMessage && (
        <Box
          marginBottom={3}
          padding={2.5}
          bgcolor='#f8f9fa'
          borderRadius={2}
          display='flex'
          alignItems='center'
          style={{
            border: '1px solid #e9ecef',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          <Box
            width={36}
            height={36}
            borderRadius='50%'
            bgcolor='#796fff'
            display='flex'
            alignItems='center'
            justifyContent='center'
            marginRight={2}
            style={{ flexShrink: 0 }}
          >
            <Typography variant='body1' style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>
              {volunteerName?.charAt(0)?.toUpperCase()}
            </Typography>
          </Box>
          <Box flex={1}>
            <Typography
              variant='subtitle2'
              style={{ color: '#3E3229', fontWeight: 600, marginBottom: 4, fontSize: '13px' }}
            >
              {volunteerName} says
            </Typography>
            <Typography variant='body2' style={{ color: '#646464', fontSize: '14px', lineHeight: 1.4 }}>
              {refereeMessageEditorState ? (
                <CampfireRichEditor readOnly editorState={refereeMessageEditorState} />
              ) : (
                refereeMessage
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Instructions */}
      {instructions && (
        <Box
          marginBottom={3}
          padding={2.5}
          bgcolor='#f8f9fa'
          borderRadius={2}
          display='flex'
          alignItems='center'
          style={{
            border: '1px solid #e9ecef',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {orgLogo ? (
            <Box
              width={36}
              height={36}
              borderRadius={1}
              display='flex'
              alignItems='center'
              justifyContent='center'
              marginRight={2}
              style={{ flexShrink: 0 }}
            >
              <img
                src={orgLogo}
                alt={orgName || 'Organization'}
                style={{
                  width: 36,
                  height: 36,
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
              />
            </Box>
          ) : (
            <Box
              width={36}
              height={36}
              borderRadius={1}
              bgcolor='#ff9237'
              display='flex'
              alignItems='center'
              justifyContent='center'
              marginRight={2}
              style={{ flexShrink: 0 }}
            >
              <Typography variant='body1' style={{ color: 'white', fontWeight: 600, fontSize: '14px' }}>
                {orgName?.charAt(0)?.toUpperCase() || 'O'}
              </Typography>
            </Box>
          )}
          <Box flex={1}>
            <Typography
              variant='subtitle2'
              style={{ color: '#3E3229', fontWeight: 600, marginBottom: 4, fontSize: '13px' }}
            >
              {orgName || 'Organization'} says
            </Typography>
            <Typography variant='body2' style={{ color: '#646464', fontSize: '14px', lineHeight: 1.4 }}>
              {instructionsEditorState ? (
                <CampfireRichEditor readOnly editorState={instructionsEditorState} />
              ) : (
                instructions
              )}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Visual separator */}
      <Box marginY={3} height={2} bgcolor='#f0f0f0' borderRadius={1} width='100%' />

      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ isValid }) => {
          const renderField = (field: TaskField) => {
            const fieldName = field.taskFieldId;
            const placeholder = field.placeholder || `Enter ${field.fieldName.toLowerCase()}`;

            return (
              <Box key={fieldName} marginBottom={3}>
                <Typography
                  variant='body1'
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: '#3E3229',
                  }}
                >
                  {field.fieldName}
                  {field.isRequired && <span style={{ color: '#f44336', marginLeft: 4 }}>*</span>}
                </Typography>
                {field.helpText && (
                  <Typography
                    variant='body2'
                    style={{
                      marginBottom: 8,
                      color: '#646464',
                    }}
                  >
                    {field.helpText}
                  </Typography>
                )}

                {field.fieldType === 'long' ? (
                  <Field name={fieldName}>
                    {({ field: formikField, meta }: FieldProps) => (
                      <TextField
                        {...formikField}
                        multiline
                        rows={4}
                        variant='outlined'
                        fullWidth
                        placeholder={placeholder}
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        style={{ maxWidth: 600 }}
                      />
                    )}
                  </Field>
                ) : field.fieldType === 'dropdown' || field.fieldType === 'multiple_choice' ? (
                  <Field name={fieldName}>
                    {({ field: formikField, meta, form }: FieldProps) => (
                      <FormControl
                        variant='outlined'
                        fullWidth
                        style={{ maxWidth: 600 }}
                        error={meta.touched && !!meta.error}
                      >
                        <InputLabel>Please select</InputLabel>
                        <Select
                          {...formikField}
                          value={formikField.value || ''}
                          onChange={(event) => {
                            form.setFieldValue(fieldName, event.target.value);
                          }}
                          label='Please select'
                        >
                          {field.selectOptions?.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {meta.touched && meta.error && (
                          <Typography variant='caption' color='error' style={{ marginTop: 4 }}>
                            {meta.error}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  </Field>
                ) : field.fieldType === 'checkbox' ? (
                  <Field name={fieldName}>
                    {({ field: formikField, meta, form }: FieldProps) => {
                      const selectedValues = formikField.value ? formikField.value.split(',').filter(Boolean) : [];
                      const handleCheckboxChange = (option: string, checked: boolean) => {
                        let newValues: string[];
                        if (checked) {
                          newValues = [...selectedValues, option];
                        } else {
                          newValues = selectedValues.filter((v: string) => v !== option);
                        }
                        form.setFieldValue(fieldName, newValues.join(','));
                      };

                      return (
                        <FormControl
                          component='fieldset'
                          error={meta.touched && !!meta.error}
                          style={{ width: '100%', maxWidth: 600 }}
                        >
                          <FormGroup>
                            {field.selectOptions?.map((option) => (
                              <FormControlLabel
                                key={option}
                                control={
                                  <Checkbox
                                    checked={selectedValues.includes(option)}
                                    onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                                    color='primary'
                                  />
                                }
                                label={option}
                                style={{ marginBottom: 4 }}
                              />
                            ))}
                          </FormGroup>
                          {meta.touched && meta.error && (
                            <Typography variant='caption' color='error' style={{ marginTop: 4 }}>
                              {meta.error}
                            </Typography>
                          )}
                        </FormControl>
                      );
                    }}
                  </Field>
                ) : (
                  <Field name={fieldName}>
                    {({ field: formikField, meta }: FieldProps) => (
                      <TextField
                        {...formikField}
                        variant='outlined'
                        fullWidth
                        placeholder={placeholder}
                        error={meta.touched && !!meta.error}
                        helperText={meta.touched && meta.error}
                        style={{ maxWidth: 600 }}
                      />
                    )}
                  </Field>
                )}
              </Box>
            );
          };

          return (
            <Form>
              <Box marginBottom={3}>
                <Typography
                  variant='h6'
                  style={{
                    fontWeight: 600,
                    marginBottom: 8,
                    color: '#3E3229',
                  }}
                >
                  {taskTitle}
                </Typography>
                <Typography
                  variant='body2'
                  style={{
                    color: '#646464',
                    marginBottom: 24,
                  }}
                >
                  Please complete all required fields below
                </Typography>
              </Box>

              {taskFields.map((field) => renderField(field))}

              <Box marginTop={3} marginBottom={0} display='flex' justifyContent={isMobile ? 'center' : 'flex-end'}>
                <TabletButton
                  type='submit'
                  variant='contained'
                  color='primary'
                  disabled={!isValid || isSubmitting}
                  startIcon={!isSubmitting ? <Send /> : undefined}
                  style={{
                    width: isMobile ? '100%' : 120,
                    height: 40,
                    fontSize: '14px',
                    fontWeight: 600,
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </TabletButton>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </Box>
  );
}
